import "@/App.css";
import { useEffect } from "react";
// Eager-load country config (kicks off /api/country-config fetch + writes
// the Mali fallback palette to CSS vars on first import).
import "./lib/countryConfig";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { PhoneShell } from "./components/PhoneShell";
import { BottomNav } from "./components/BottomNav";
import { PanicProvider } from "./components/PanicProvider";
import { OnboardingGate } from "./components/OnboardingGate";
import { GpsPermissionGate } from "./components/GpsPermissionGate";
import HomePage from "./pages/HomePage";
import ContactsPage from "./pages/ContactsPage";
import ProfilPage from "./pages/ProfilPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import SharePage from "./pages/SharePage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminPanelPage from "./pages/AdminPanelPage";
import AgencyLoginPage from "./pages/AgencyLoginPage";
import AgencyDashboardPage from "./pages/AgencyDashboardPage";
import AgencyMapPage from "./pages/AgencyMapPage";
import MapPage from "./pages/MapPage";
import LiveViewPage from "./pages/LiveViewPage";
import BroadcastDetailPage from "./pages/BroadcastDetailPage";
import AlertStatusPage from "./pages/AlertStatusPage";
import MessagesPage from "./pages/MessagesPage";
import { LiveBroadcastBanner } from "./components/LiveBroadcastBanner";
import { useCitizenBroadcasts } from "./lib/useCitizenBroadcasts";

/**
 * Eagerly request the browser geolocation permission as soon as the app
 * loads — BEFORE the onboarding gate. This way the Mali user sees the
 * native "Allow location?" prompt in the first second of opening the PWA
 * instead of having to dismiss the gate first. We also re-prompt on
 * Permissions API state changes so a user who initially denied can flip
 * the setting in browser UI without rebooting the tab.
 */
function useEagerGeolocationPrompt() {
  useEffect(() => {
    if (!("geolocation" in navigator)) return undefined;
    // Fire the prompt straight away. We don't keep the position — the
    // HomePage `useGeolocation` hook owns the live coordinates; this call
    // exists solely to surface the browser permission dialog.
    navigator.geolocation.getCurrentPosition(
      () => {},
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
    // If the Permissions API is available, re-prompt the moment a user
    // flips a previously-denied permission to "prompt" / "granted".
    let permStatus = null;
    const onChange = () => {
      if (permStatus?.state === "prompt") {
        navigator.geolocation.getCurrentPosition(
          () => {},
          () => {},
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
      }
    };
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          permStatus = status;
          status.addEventListener?.("change", onChange);
        })
        .catch((err) => console.error("permissions.query failed", err));
    }
    return () => {
      permStatus?.removeEventListener?.("change", onChange);
    };
  }, []);
}

function CitizenLiveLayer() {
  // Mounts the citizen-side WS firehose and renders the slide-down
  // banner globally so it appears no matter which page is active.
  // We try to silently unlock the AudioContext on first user gesture
  // so the broadcast sound can play on the very next push.
  const { incoming, dismiss } = useCitizenBroadcasts({ enabled: true });
  useEffect(() => {
    let unlocked = false;
    const tryUnlock = async () => {
      if (unlocked) return;
      try {
        const { notificationAudio } = await import("./lib/notificationSound");
        const ok = await notificationAudio.unlock();
        if (ok) unlocked = true;
      } catch (_e) { /* noop */ }
      // Same gesture is our chance to ask for Notification permission +
      // register the Web Push SW so future broadcasts arrive even with
      // the tab in the background. Citizen-role only.
      try {
        const { userIdentity } = await import("./lib/identity");
        const phone = userIdentity.getPhone();
        if (phone) {
          const { subscribeCitizen } = await import("./lib/pushSubscribe");
          subscribeCitizen(phone);
        }
      } catch (_e) { /* noop */ }
    };
    const opts = { once: true, capture: true };
    window.addEventListener("pointerdown", tryUnlock, opts);
    window.addEventListener("keydown", tryUnlock, opts);
    return () => {
      window.removeEventListener("pointerdown", tryUnlock, opts);
      window.removeEventListener("keydown", tryUnlock, opts);
    };
  }, []);
  return <LiveBroadcastBanner broadcast={incoming} onDismiss={dismiss} />;
}

function App() {
  useEagerGeolocationPrompt();
  return (
    <BrowserRouter>
      <Routes>
        {/* Public viewer for shared "Live View" links — no onboarding gate */}
        <Route path="/live/:token" element={<LiveViewPage />} />
        {/* Everything else gets the citizen app shell */}
        <Route
          path="*"
          element={
            <OnboardingGate>
              <PanicProvider>
                <PhoneShell>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/contacts" element={<ContactsPage />} />
                    <Route path="/profil" element={<ProfilPage />} />
                    <Route path="/history" element={<HistoryPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/carte" element={<MapPage />} />
                    <Route path="/share/:id" element={<SharePage />} />
                    <Route path="/broadcasts/:id" element={<BroadcastDetailPage />} />
                    <Route path="/alerts/:alertId/status" element={<AlertStatusPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/admin/login" element={<AdminLoginPage />} />
                    <Route path="/admin" element={<AdminPanelPage />} />
                    <Route path="/admin/:tab" element={<AdminPanelPage />} />
                    <Route path="/admin/agency/login" element={<AgencyLoginPage />} />
                    <Route path="/admin/agency" element={<AgencyDashboardPage />} />
                    <Route path="/agence/carte" element={<AgencyMapPage />} />
                  </Routes>
                  <CitizenLiveLayer />
                  <BottomNav />
                  <GpsPermissionGate />
                </PhoneShell>
              </PanicProvider>
            </OnboardingGate>
          }
        />
      </Routes>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#fff",
            border: "2px solid #0A0A0A",
            borderRadius: 0,
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 13,
            letterSpacing: "0.02em",
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
